/**
 * GlobalSearchBar — site-wide search omnibar for the header.
 *
 * Desktop: renders as an inline pill in the header.
 * Mobile: renders as a search icon that expands into a full-width overlay.
 *
 * Type-ahead dropdown groups results by type (Products, Restaurants, Articles,
 * Recipes, Deals, Places, News). "See all X results" link opens
 * `/food-pulse/search?q=…`.
 *
 * Debounced 220 ms; cancels stale requests via AbortController.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Loader2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const GROUP_ICON = {
  food_item:     "🥬",
  food_business: "🏬",
  food_article:  "📰",
  recipe:        "🍳",
  deal:          "🏷️",
  place:         "📍",
  news:          "📄",
};

function ResultRow({ hit, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(hit)}
      className="w-full flex items-center gap-3 px-3 py-2 hover:bg-amber-50 rounded-lg text-left"
      data-testid={`global-search-hit-${hit.type}-${hit.id}`}
    >
      {hit.image ? (
        <img src={hit.image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
      ) : (
        <div className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0 text-lg" style={{ background: "#F5F0E4" }}>
          {GROUP_ICON[hit.type] || "•"}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: "#1A3B1A" }}>{hit.title}</div>
        {hit.subtitle && <div className="text-[11px] opacity-70 truncate">{hit.subtitle}</div>}
      </div>
    </button>
  );
}

export default function GlobalSearchBar({ compact = false, className = "" }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null); // {groups, total} or null
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const abortRef = useRef(null);
  const wrapRef  = useRef(null);
  const nav = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        if (compact) setExpanded(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [compact]);

  // Debounced fetch
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      abortRef.current?.abort?.();
      const controller = new AbortController();
      abortRef.current = controller;
      axios.get(`${API}/api/search`, {
        params: { q: q.trim(), per_type: 3 },
        signal: controller.signal,
      })
      .then((r) => { setResults(r.data); setLoading(false); })
      .catch((e) => {
        if (e.name !== "CanceledError" && e.code !== "ERR_CANCELED") {
          setResults({ groups: [], total: 0 });
          setLoading(false);
        }
      });
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const goToFullResults = () => {
    if (q.trim().length < 2) return;
    setOpen(false);
    if (compact) setExpanded(false);
    nav(`/food-pulse/search?q=${encodeURIComponent(q.trim())}`);
  };

  const onSelect = (hit) => {
    setOpen(false);
    if (compact) setExpanded(false);
    setQ("");
    nav(hit.url);
  };

  const onSubmit = (e) => { e.preventDefault(); goToFullResults(); };

  // Compact/collapsed on mobile — render just the icon
  if (compact && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        className={`p-2 rounded-full hover:bg-black/5 transition ${className}`}
        aria-label="Search"
        data-testid="global-search-icon"
      >
        <Search className="w-5 h-5" style={{ color: "#0F3D2E" }} />
      </button>
    );
  }

  return (
    <div ref={wrapRef} className={`relative ${className}`} data-testid="global-search-bar">
      <form onSubmit={onSubmit} className="relative flex items-center">
        <Search className="w-4 h-4 absolute left-3 opacity-50 pointer-events-none" style={{ color: "#0F3D2E" }} />
        <input
          type="search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          autoFocus={compact}
          placeholder="Search products, restaurants, recipes…"
          className="w-full rounded-full border pl-9 pr-9 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-amber-300"
          style={{ borderColor: "#E4DDD1", minWidth: compact ? 260 : 300 }}
          data-testid="global-search-input"
        />
        {q && (
          <button type="button" onClick={() => { setQ(""); setResults(null); }}
                  className="absolute right-2 p-1 rounded-full hover:bg-black/5"
                  aria-label="Clear" data-testid="global-search-clear">
            <X className="w-3.5 h-3.5 opacity-60" />
          </button>
        )}
      </form>

      {open && q.trim().length >= 2 && (
        <div className="absolute top-full mt-2 right-0 w-[min(560px,90vw)] bg-white rounded-2xl shadow-xl border p-2 z-50 max-h-[70vh] overflow-y-auto"
             style={{ borderColor: "#E4DDD1" }}
             data-testid="global-search-dropdown">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-3 text-sm opacity-70" data-testid="global-search-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Searching…
            </div>
          )}
          {!loading && results && results.total === 0 && (
            <div className="px-3 py-6 text-center text-sm opacity-70" data-testid="global-search-empty">
              No matches for "<b>{q}</b>". Try a different word.
            </div>
          )}
          {!loading && results && results.groups.map((g) => (
            <div key={g.type} className="mb-2" data-testid={`global-search-group-${g.type}`}>
              <div className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest opacity-60 flex items-center gap-1.5">
                <span>{GROUP_ICON[g.type] || "•"}</span> {g.label} · {g.count}
              </div>
              {g.items.map((hit) => (
                <ResultRow key={`${hit.type}-${hit.id}`} hit={hit} onSelect={onSelect} />
              ))}
            </div>
          ))}
          {!loading && results && results.total > 0 && (
            <button
              type="button"
              onClick={goToFullResults}
              className="w-full mt-1 px-3 py-2.5 rounded-lg font-semibold text-sm text-white text-center"
              style={{ background: "#0F3D2E" }}
              data-testid="global-search-see-all"
            >
              See all {results.total} results →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
